import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/Icon';
import { balloon } from '@/config/balloon';
import { useSettingsStore } from '@/settings/store';
import { useWindowStore } from '@/wm/store';
import { play } from '@/lib/sound';
import css from './Tray.module.css';

/**
 * XP's "Take a tour" notification, once per visitor. Every exit route marks
 * the flag so it never comes back.
 */
export function BalloonTip() {
  const seen = useSettingsStore((s) => s.seen.balloon);
  const markSeen = useSettingsStore((s) => s.markSeen);

  const [visible, setVisible] = useState(false);
  // Auto-dismiss is suspended while the visitor is reading — pointer over the
  // balloon, or the keyboard inside it.
  const [hovered, setHovered] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  // StrictMode mounts effects twice in dev; the chime must still ring once.
  const chimed = useRef(false);
  const dismissed = useRef(false);

  const dismiss = useCallback(() => {
    if (dismissed.current) return;
    dismissed.current = true;
    // Unmounting the focused button would drop the keyboard on document.body
    // and restart tabbing from the top of the page. Hand it to the tray, which
    // is where the balloon's tail points anyway.
    const root = rootRef.current;
    if (root && root.contains(document.activeElement)) {
      const tray = root.parentElement;
      const next = tray
        ? Array.from(tray.querySelectorAll<HTMLElement>('button')).find(
            (el) => !root.contains(el),
          )
        : undefined;
      next?.focus({ preventScroll: true });
    }
    setVisible(false);
    markSeen('balloon');
  }, [markSeen]);

  useEffect(() => {
    if (seen) return;
    const timer = window.setTimeout(() => {
      setVisible(true);
      if (!chimed.current) {
        chimed.current = true;
        play('balloon');
      }
    }, balloon.delayMs);
    return () => window.clearTimeout(timer);
  }, [seen]);

  useEffect(() => {
    if (!visible || hovered || focusWithin) return;
    const timer = window.setTimeout(dismiss, balloon.timeoutMs);
    return () => window.clearTimeout(timer);
  }, [visible, hovered, focusWithin, dismiss]);

  const activate = useCallback(() => {
    useWindowStore.getState().open(balloon.action.appId, { ...balloon.action.args });
    dismiss();
  }, [dismiss]);

  if (seen || !visible) return null;

  return (
    <div
      ref={rootRef}
      className={css.balloon}
      role="status"
      aria-live="polite"
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onFocus={() => setFocusWithin(true)}
      onBlur={(e) => setFocusWithin(e.currentTarget.contains(e.relatedTarget))}
    >
      <button type="button" className={css.balloonBody} onClick={activate}>
        <span className={css.balloonTitle}>
          <Icon src="/assets/icons/msg-info.svg" size={16} />
          {balloon.title}
        </span>
        <span className={css.balloonText}>{balloon.body}</span>
      </button>
      <button
        type="button"
        className={css.balloonClose}
        aria-label="Close notification"
        onClick={dismiss}
      >
        &times;
      </button>
      <span className={css.tail} aria-hidden="true" />
    </div>
  );
}
